import { cn } from "@devhop/ui";
import { MinusIcon, TrendingDownIcon, TrendingUpIcon } from "lucide-react";
import type { EventFeaturesDTO, EventProfitLossDTO } from "../../domain/contracts";
import type { CurrencyAmountBuckets } from "../../domain/contracts/reports";
import { eventHasFeature } from "../../domain/services/event-features";
import {
  displayModeLabel,
  formatFxRateSummary,
  formatSignedProfit,
  pickProfitSnapshot,
  profitHeadline,
  profitHeroBgClass,
  profitTone,
  profitToneClass,
  type ProfitDisplayMode,
  type ProfitLossView,
  viewHint,
  viewLabel,
} from "../lib/event-profit-loss-ui";
import {
  formatCurrencyBucketsInline,
  formatPerformanceMoney,
} from "./PerformanceReportsNav";

type EventProfitLossHeroProps = {
  profitLoss: EventProfitLossDTO;
  collected: CurrencyAmountBuckets;
  invoiced: CurrencyAmountBuckets;
  view: ProfitLossView;
  displayMode: ProfitDisplayMode;
  onViewChange: (view: ProfitLossView) => void;
  onDisplayModeChange: (mode: ProfitDisplayMode) => void;
  eventFeatures: EventFeaturesDTO;
  canReadDocuments: boolean;
};

export function EventProfitLossHero({
  profitLoss,
  collected,
  invoiced,
  view,
  displayMode,
  onViewChange,
  onDisplayModeChange,
  eventFeatures,
  canReadDocuments,
}: EventProfitLossHeroProps) {
  const snapshot = pickProfitSnapshot(profitLoss, view, displayMode);
  const tone = profitTone(snapshot.profit);
  const hasDocuments = eventHasFeature(eventFeatures, "documents");
  const converted = profitLoss.converted;
  const revenueView =
    converted &&
    (view === "realized"
      ? converted.revenueCollected
      : converted.revenueProjected);
  const showConvertedMode =
    profitLoss.hasForeignRevenue && converted != null;
  const usingConverted =
    displayMode === "converted" && showConvertedMode;
  const fxSummary =
    profitLoss.fxRates && usingConverted
      ? formatFxRateSummary(profitLoss.fxRates)
      : null;

  return (
    <section
      className={cn(
        "overflow-hidden rounded-2xl border-2 px-6 py-6 sm:px-8 sm:py-8",
        profitHeroBgClass(tone),
      )}
      aria-label="ກຳໄລ/ຂາດທຶນ"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="font-semibold text-muted-foreground text-xs uppercase tracking-widest">
            {profitHeadline(snapshot.profit)} · {viewLabel(view)}
            {usingConverted ? " · LAK ເທົ່າຄ່າ" : ""}
          </p>
          <p className="mt-2 font-bold text-4xl tracking-tight tabular-nums sm:text-5xl">
            <span className={profitToneClass(tone)}>
              {formatSignedProfit(snapshot.profit)}
            </span>
          </p>
          {snapshot.profitPercent != null ? (
            <p className={cn("mt-2 text-sm tabular-nums", profitToneClass(tone))}>
              {snapshot.profit != null && snapshot.profit > 0 ? (
                <TrendingUpIcon className="mr-1 inline size-4" />
              ) : snapshot.profit != null && snapshot.profit < 0 ? (
                <TrendingDownIcon className="mr-1 inline size-4" />
              ) : (
                <MinusIcon className="mr-1 inline size-4" />
              )}
              {snapshot.profitPercent}% ຂອງລາຍຮັບ
            </p>
          ) : null}
          {fxSummary ? (
            <p className="mt-2 text-muted-foreground text-sm">
              ຄິດຈາກອັດຕາ: {fxSummary}
            </p>
          ) : null}
          <p className="mt-3 max-w-xl text-muted-foreground text-sm">{viewHint(view)}</p>
        </div>

        <div className="flex shrink-0 flex-col items-end gap-2">
          {showConvertedMode ? (
            <div className="flex rounded-lg border bg-background/80 p-1">
              {(["native", "converted"] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  className={cn(
                    "rounded-md px-3 py-1.5 text-sm transition-colors",
                    displayMode === option
                      ? "bg-primary font-medium text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                  onClick={() => onDisplayModeChange(option)}
                >
                  {displayModeLabel(option)}
                </button>
              ))}
            </div>
          ) : null}
          <div className="flex rounded-lg border bg-background/80 p-1">
            {(["realized", "projected"] as const).map((option) => (
              <button
                key={option}
                type="button"
                className={cn(
                  "rounded-md px-3 py-1.5 text-sm transition-colors",
                  view === option
                    ? "bg-primary font-medium text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
                onClick={() => onViewChange(option)}
              >
                {viewLabel(option)}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 grid gap-3 border-t border-border/60 pt-5 sm:grid-cols-2">
        <SummaryChip
          label={
            usingConverted
              ? view === "realized"
                ? "ລາຍຮັບ LAK ເທົ່າຄ່າ (ເກັບແລ້ວ)"
                : "ລາຍຮັບ LAK ເທົ່າຄ່າ (ຄາດ)"
              : view === "realized"
                ? "ລາຍຮັບ (ເກັບແລ້ວ LAK)"
                : "ລາຍຮັບຄາດ (LAK)"
          }
          value={
            usingConverted && revenueView?.lakEquivalent != null
              ? formatPerformanceMoney(revenueView.lakEquivalent, "LAK")
              : formatPerformanceMoney(snapshot.revenueLak, "LAK")
          }
          hint={
            view === "projected" && profitLoss.revenueInvoicedLak <= 0
              ? "ໃຊ້ໃບສະເໜີເມື່ອຍັງບໍ່ມີ INV"
              : usingConverted && revenueView
                ? formatCurrencyBucketsInline(revenueView.native)
                : undefined
          }
        />
        <SummaryChip
          label={view === "realized" ? "ຕົ້ນທຸນ (ຈ່າຍແລ້ວ)" : "ຕົ້ນທຸນ (commit)"}
          value={formatPerformanceMoney(snapshot.costs, "LAK")}
        />
      </div>

      {profitLoss.hasForeignRevenue && snapshot.profit == null ? (
        <p className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-amber-950 text-sm dark:text-amber-100">
          {usingConverted && revenueView && !revenueView.conversionComplete
            ? "ມີລາຍຮັບສະກຸນຕ່າງປະເທດ — ປ້ອນອັດຕາແລກປ່ຽນໃຫ້ຄົບທຸກສະກຸນທີ່ມີລາຍຮັບ"
            : "ມີລາຍຮັບສະກຸນຕ່າງປະເທດ — ກຳໄລ LAK ຄິດໄດ້ເມື່ອມີເງິນເກັບ LAK ຫຼື INV/QT LAK ຫຼື ຕັ້ງອັດຕາແລກປ່ຽນ"}
        </p>
      ) : null}

      {canReadDocuments && hasDocuments && profitLoss.hasForeignRevenue ? (
        <p className="mt-4 text-muted-foreground text-xs">
          ເກັບແລ້ວ:{" "}
          <span className="font-medium text-foreground">
            {formatCurrencyBucketsInline(collected)}
          </span>
          {profitLoss.revenueInvoicedLak > 0 ||
          invoiced.USD > 0 ||
          invoiced.THB > 0 ? (
            <>
              {" "}
              · INV{" "}
              <span className="font-medium text-foreground">
                {formatCurrencyBucketsInline(invoiced)}
              </span>
            </>
          ) : null}
        </p>
      ) : canReadDocuments && hasDocuments && collected.LAK > 0 ? (
        <p className="mt-4 text-muted-foreground text-xs">
          ເກັບ LAK ແລ້ວ{" "}
          <span className="font-medium text-foreground tabular-nums">
            {formatPerformanceMoney(collected.LAK, "LAK")}
          </span>
          {profitLoss.revenueInvoicedLak > 0 ? (
            <>
              {" "}
              · INV LAK{" "}
              <span className="font-medium text-foreground tabular-nums">
                {formatPerformanceMoney(profitLoss.revenueInvoicedLak, "LAK")}
              </span>
            </>
          ) : null}
        </p>
      ) : null}
    </section>
  );
}

function SummaryChip({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded-lg border bg-background/70 px-4 py-3">
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className="mt-1 font-semibold text-lg tabular-nums">{value}</p>
      {hint ? <p className="mt-1 text-muted-foreground text-xs">{hint}</p> : null}
    </div>
  );
}
